import { useLayoutEffect } from "react";
import { useLocation, useSearchParams } from "react-router";
import { useAppSelector } from "@/app/hooks";
import { selectAuthActor } from "@/features/auth/authSessionSlice";
import { useGetMerchantContextQuery } from "@/features/chat/merchantContextApi";
import { readPendingHomeIntent } from "@/features/chat/pendingHomeIntent";
import { findBindingForContextShop } from "@/features/chat/merchantContextStore";
import type { MerchantContextSummary } from "@/features/chat/merchantContextSchemas";
import type { MerchantStoreBinding } from "@/features/settings/storeManagementSchemas";
import { selectActiveStoreBindingId } from "@/features/settings/activeStoreSlice";
import { useGetMerchantStoreBindingsQuery } from "@/features/settings/storeManagementApi";
import { merchantHomeIntentScopeKey, useGetMerchantHomeQuery } from "./merchantHomeApi";
import { merchantHomeWorkbenchCardTypeSchema } from "./merchantHomeSchemas";
import { merchantAdAccountBindingIDSchema } from "./workbench/contentSchemas";
import { useWorkbenchContent } from "./workbench/useWorkbenchContent";
import { projectMerchantHomeSurfaces } from "./workbench/catalogAvailability";
import { readWorkbenchContentQueryFailure } from "./workbench/workbenchContentQueryFailure";

// 工作台读取 27 类 canonical 内容；legacy `/opportunities` 仍由独立 8 类 schema 拥有。
const canonicalCardTypes = [...merchantHomeWorkbenchCardTypeSchema.options];
const marketShopBindingQueryKey = "market_shop_binding_id";

export function parseMerchantHomeAdAccountBindingId(values: readonly string[]) {
  if (values.length !== 1) return undefined;
  const parsed = merchantAdAccountBindingIDSchema.safeParse(values[0]);
  return parsed.success ? parsed.data : undefined;
}

export function merchantHomeCountryCode(value: string) {
  const code = value.trim().toUpperCase();
  return /^[A-Z]{2}$/u.test(code) ? code : "";
}

export function mayNormalizeMerchantHomeUrl(pathname: string, pendingState?: string) {
  return pathname === "/workspace" && pendingState !== "handed_off";
}

export function resolveMerchantHomeUrlScope(input: {
  bindingId: string;
  bindingRegion: string;
  conversationId: string;
  urlMarket: string;
  urlCategory: string;
  urlMarketShopBindingId: string;
  resolvedMarket?: string;
  resolvedCategory?: string;
}) {
  const urlMarket = merchantHomeCountryCode(input.urlMarket);
  const urlCategory = input.urlCategory.trim();
  const bindingMarket = merchantHomeCountryCode(input.bindingRegion);
  const shopHome = !input.conversationId && Boolean(input.bindingId);
  const ownTarget = shopHome && input.urlMarketShopBindingId === input.bindingId && Boolean(urlMarket);
  const foreignTarget = shopHome && Boolean(input.urlMarketShopBindingId) && input.urlMarketShopBindingId !== input.bindingId;
  const staleScope = shopHome && (foreignTarget || !ownTarget && (
    Boolean(input.urlMarket.trim()) && (!bindingMarket || urlMarket !== bindingMarket) || Boolean(urlCategory)
  ));
  // 无归属的异店 URL 不能先打出错误市场的 card-content 请求；只有当前店主动选择的目标可以跨市场。
  const market = shopHome ? ownTarget ? urlMarket : bindingMarket
    : urlMarket || merchantHomeCountryCode(input.resolvedMarket ?? "");
  const category = staleScope ? (input.resolvedCategory ?? "").trim() : urlCategory || (input.resolvedCategory ?? "").trim();
  return {
    market,
    category,
    marketShopBindingId: ownTarget ? input.bindingId : "",
    clearAdAccount: staleScope,
  };
}

export function resolveMerchantHomeBinding(
  bindings: readonly MerchantStoreBinding[] | undefined,
  selected: string,
  conversationId: string,
  context: MerchantContextSummary | undefined,
  organizationId: string,
  userId: string,
) {
  if (conversationId) {
    // 对话店铺是独立业务范围；缺失或撤权时绝不借用首页选择。
    if (context?.conversationId !== conversationId || context.organizationId !== organizationId || context.userId !== userId) return undefined;
    const binding = findBindingForContextShop(bindings ?? [], context.selectedShop);
    return binding?.status === "active" ? binding : undefined;
  }
  return bindings?.find(item => item.id === selected) ?? (bindings?.length === 1 ? bindings[0] : undefined);
}

export function useMerchantHomeScope(pollingInterval = 30_000, conversationId = "") {
  const actor = useAppSelector(selectAuthActor);
  const userId = actor?.userId ?? "";
  const organizationId = actor?.activeOrganizationId ?? "";
  const selected = useAppSelector(state => selectActiveStoreBindingId(state, organizationId));
  const stores = useGetMerchantStoreBindingsQuery(organizationId, { skip: !organizationId });
  const bindings = stores.currentData?.bindings;
  const conversationContext = useGetMerchantContextQuery(
    { conversationId, organizationId },
    { skip: !conversationId || !organizationId },
  );
  const binding = resolveMerchantHomeBinding(bindings, selected, conversationId, conversationContext.currentData, organizationId, userId);
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const urlMarket = params.get("market") ?? "";
  const urlCategory = params.get("category") ?? "";
  const urlMarketShopBindingId = params.getAll(marketShopBindingQueryKey).length === 1 ? params.get(marketShopBindingQueryKey) ?? "" : "";
  const contextFailed = Boolean(conversationId && conversationContext.isError);
  const selectionRequired = Boolean(conversationId && conversationContext.currentData && !binding && !contextFailed);
  const skip = !userId || !organizationId || !stores.currentData || stores.isError || Boolean(conversationId && (!binding || contextFailed || conversationContext.isFetching));
  // overview 不把 URL 市场/品类放进 query key：继承写入 ?market= 后仍复用同一店铺回执，避免整页打成 loading。
  // 工作计数与策略按店铺读取，List 也忽略 market；card-content 使用店铺归属校正后的范围。
  const overview = useGetMerchantHomeQuery({ userId, organizationId, shopBindingId: binding?.id, market: "", category: "" }, { skip, pollingInterval, skipPollingIfUnfocused: true, refetchOnFocus: true, refetchOnReconnect: true });
  const urlScope = resolveMerchantHomeUrlScope({
    bindingId: binding?.id ?? "", bindingRegion: binding?.region ?? "", conversationId,
    urlMarket, urlCategory, urlMarketShopBindingId,
    resolvedMarket: overview.currentData?.resolved_scope.market,
    resolvedCategory: overview.currentData?.resolved_scope.category,
  });
  const { market, category } = urlScope;
  const adAccountBindingId = urlScope.clearAdAccount ? undefined : parseMerchantHomeAdAccountBindingId(params.getAll("ad_account_binding_id"));
  const query = { userId, organizationId, shopBindingId: binding?.id, market, category };
  const opportunityScope = query;
  const contentSkipped = skip || !overview.currentData || overview.isError;
  const content = useWorkbenchContent({ ...opportunityScope, adAccountBindingId, cardTypes: canonicalCardTypes }, contentSkipped, pollingInterval);
  const contentFailure = readWorkbenchContentQueryFailure(content.query.error, { skipped: contentSkipped });
  const contentReadError = stores.isError || overview.isError || content.query.isError;
  const contentReadErrorCode = stores.isError ? "store_bindings_read_failed"
    : overview.isError ? "merchant_home_overview_read_failed" : contentFailure?.code;
  const retryContentRead = () => {
    if (stores.isError) void stores.refetch();
    else if (overview.isError) void overview.refetch();
    else content.retry();
  };
  const homeProjection = projectMerchantHomeSurfaces(
    skip || content.query.isError || overview.isError || stores.isError ? [] : content.groups,
  );
  const setMarket = (nextMarket: string, nextCategory: string) => setParams(previous => {
    const next = new URLSearchParams(previous);
    if (nextMarket) next.set("market", nextMarket); else next.delete("market");
    if (nextCategory) next.set("category", nextCategory); else next.delete("category");
    if (!conversationId && binding?.id && nextMarket) next.set(marketShopBindingQueryKey, binding.id);
    else next.delete(marketShopBindingQueryKey);
    return next;
  }, { replace: true, state: location.state });
  useLayoutEffect(() => {
    // 登录前草稿已交接给新会话时，首页的迟到规范化不能覆盖正在提交的会话路由。
    if (conversationId || !mayNormalizeMerchantHomeUrl(window.location.pathname, readPendingHomeIntent()?.state) || overview.isError || !overview.currentData) return;
    if (urlMarket === market && urlCategory === category && urlMarketShopBindingId === urlScope.marketShopBindingId && !urlScope.clearAdAccount) return;
    setParams(previous => {
      const params = new URLSearchParams(previous);
      if (market) params.set("market", market); else params.delete("market");
      if (category) params.set("category", category); else params.delete("category");
      params.delete(marketShopBindingQueryKey);
      if (urlScope.marketShopBindingId) params.set(marketShopBindingQueryKey, urlScope.marketShopBindingId);
      if (urlScope.clearAdAccount) params.delete("ad_account_binding_id");
      return params;
    }, { replace: true, state: location.state });
  }, [conversationId, overview.isError, overview.currentData, urlMarket, urlCategory, urlMarketShopBindingId, market, category, urlScope.marketShopBindingId, urlScope.clearAdAccount, setParams, location.state]);
  const setAdAccountBindingId = (nextAdAccountBindingId: string) => {
    if (nextAdAccountBindingId && !merchantAdAccountBindingIDSchema.safeParse(nextAdAccountBindingId).success) return;
    setParams(previous => {
      const next = new URLSearchParams(previous);
      if (nextAdAccountBindingId) next.set("ad_account_binding_id", nextAdAccountBindingId); else next.delete("ad_account_binding_id");
      return next;
    }, { replace: true, state: location.state });
  };
  return {
    query,
    opportunityScope,
    intentScopeKey: merchantHomeIntentScopeKey(opportunityScope),
    scopeKey: `${userId}:${organizationId}:${binding?.id ?? ""}:${conversationId}:${adAccountBindingId ?? ""}`,
    content,
    contentFailure,
    contentReadError,
    contentReadErrorCode,
    retryContentRead,
    // cards 是兼容别名；新消费方必须按 surface 显式选择 homeProjection 或 allCards。
    cards: homeProjection.allCards,
    allCards: homeProjection.allCards,
    homeProjection,
    overview,
    stores,
    binding,
    conversationContext,
    conversationId,
    contextFailed,
    selectionRequired,
    skip,
    contentSkipped,
    adAccountBindingId,
    setAdAccountBindingId,
    setMarket,
  };
}
